/**
 * Central analytics layer.
 *
 * Responsibilities:
 *  - Ensure window.dataLayer exists.
 *  - Manage marketing consent (Consent Mode v2 signal).
 *  - Push business events to dataLayer.
 *
 * GTM script itself is injected ONCE in src/routes/__root.tsx — this module
 * never injects the GTM loader to avoid double-loading.
 *
 * Tags (GA4, Meta, TikTok, Clarity) are configured inside GTM and respect
 * the consent state pushed here.
 */

import { sanitizeParams } from "./analytics-sanitize";

type DLEvent = Record<string, unknown> & { event: string };

declare global {
  interface Window {
    dataLayer?: unknown[];
  }
}

const CONSENT_KEY = "consent_marketing"; // "granted" | "denied"

export type ConsentState = "granted" | "denied" | "unset";

function isBrowser(): boolean {
  return typeof window !== "undefined";
}

function ensureDataLayer(): unknown[] | null {
  if (!isBrowser()) return null;
  if (!window.dataLayer) window.dataLayer = [];
  return window.dataLayer;
}

function push(evt: DLEvent): void {
  const dl = ensureDataLayer();
  if (!dl) return;
  dl.push(sanitizeParams(evt));
}

/* ---------------- Consent ---------------- */

export function getMarketingConsent(): ConsentState {
  if (!isBrowser()) return "unset";
  try {
    const v = window.localStorage.getItem(CONSENT_KEY);
    if (v === "granted" || v === "denied") return v;
    return "unset";
  } catch {
    return "unset";
  }
}

/**
 * Update consent. Pushes a "consent_update" event so a GTM Custom Event
 * trigger can call gtag('consent','update', …) inside the container.
 */
export function setMarketingConsent(granted: boolean): void {
  if (!isBrowser()) return;
  try {
    window.localStorage.setItem(CONSENT_KEY, granted ? "granted" : "denied");
  } catch {
    /* ignore */
  }

  const state = granted ? "granted" : "denied";

  // Native gtag-style Consent Mode v2 command. GTM listens for arguments-shaped
  // pushes on dataLayer and routes them through gtag('consent','update', …).
  // Pushed RAW (not via sanitizeParams) because it is a positional array, not an event object.
  const dl = ensureDataLayer();
  if (dl) {
    dl.push([
      "consent",
      "update",
      {
        ad_storage: state,
        analytics_storage: state,
        ad_user_data: state,
        ad_personalization: state,
      },
    ]);
  }

  // Debug/custom-trigger event (kept for GTM Custom Event triggers + diagnostics).
  push({
    event: "consent_update",
    consent_marketing: state,
    ad_storage: state,
    analytics_storage: state,
    ad_user_data: state,
    ad_personalization: state,
  });
}

/**
 * Initialize the dataLayer on first call. Idempotent. Safe on SSR (no-op).
 * Does NOT inject the GTM script — that lives in __root.tsx.
 */
export function initAnalytics(): void {
  if (!isBrowser()) return;
  ensureDataLayer();
}

/* ---------------- Event API ---------------- */

export function trackEvent(name: string, params: Record<string, unknown> = {}): void {
  push({ event: name, ...params });
}

export function trackPageView(path: string, title?: string): void {
  push({
    event: "page_view",
    page_path: path,
    page_title: title ?? (isBrowser() ? document.title : undefined),
  });
}

type CartLikeItem = {
  id: string;
  slug?: string;
  name: string;
  price: number;
  type?: "package" | "service";
};

function toGa4Item(it: CartLikeItem) {
  return {
    item_id: it.id,
    item_name: it.name,
    price: Number(it.price) || 0,
    quantity: 1,
    item_category: it.type ?? undefined,
  };
}

export function trackViewItemList(listName: string, items: CartLikeItem[]): void {
  push({
    event: "view_item_list",
    item_list_name: listName,
    items: items.map(toGa4Item),
  });
}

export function trackAddToCart(item: CartLikeItem): void {
  push({
    event: "add_to_cart",
    currency: "USD",
    value: Number(item.price) || 0,
    items: [toGa4Item(item)],
  });
}

export function trackViewCart(items: CartLikeItem[], value: number): void {
  push({
    event: "view_cart",
    currency: "USD",
    value: Number(value) || 0,
    items: items.map(toGa4Item),
  });
}

export function trackBeginCheckout(items: CartLikeItem[], value: number): void {
  push({
    event: "begin_checkout",
    currency: "USD",
    value: Number(value) || 0,
    items: items.map(toGa4Item),
  });
}

export function trackCheckoutContinueToStripe(value: number): void {
  push({
    event: "checkout_continue_to_stripe",
    currency: "USD",
    value: Number(value) || 0,
  });
}

type PurchaseOrder = {
  id?: string;
  order_short?: string;
  total: number;
  items?: Array<{ id?: string; name: string; price: number; type?: string }>;
};

export function trackPurchase(order: PurchaseOrder): void {
  const items = (order.items ?? []).map((it, idx) => ({
    item_id: it.id ?? `${order.order_short ?? "item"}-${idx}`,
    item_name: it.name,
    price: Number(it.price) || 0,
    quantity: 1,
    item_category: it.type,
  }));
  push({
    event: "purchase",
    transaction_id: order.order_short ?? order.id ?? "unknown",
    currency: "USD",
    value: Number(order.total) || 0,
    items,
  });
}

/**
 * Intake events never carry the real intake token.
 * The token in the URL is a private link to a specific order — sending it
 * to analytics would be PII / access-control leakage.
 */
export function trackIntakeOpened(): void {
  push({ event: "intake_opened", intake_id: "redacted" });
}

export function trackIntakeSubmitted(): void {
  push({ event: "intake_submitted", intake_id: "redacted" });
}

export function trackWhatsAppClick(location: string): void {
  push({ event: "whatsapp_click", click_location: location });
}
